import { useState, useMemo, forwardRef, useEffect, useCallback } from 'react';
import {
  FormControl,
  Select,
  MenuItem,
  ListSubheader,
  TextField,
  InputAdornment,
  InputLabel,
  Checkbox,
  ListItemText,
  Typography
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { getAllProjectsFilter } from 'src/services/projectDetailService';

interface ProjectDropdownFilterProps {
  title: string;
  value: string;
  onChange: (value: string) => void;
  width?: string;
  disabled?: boolean;
  projectId?: string;
  customerIds?: string[];
}

const ProjectDropdownFilter = forwardRef((props: ProjectDropdownFilterProps, _ref) => {
  const [searchText, setSearchText] = useState<string>('');
  const [list, setList] = useState([]);
  const [prevCustomerIds, setPrevCustomerIds] = useState<string[]>(props.customerIds || []);

  const selectedValues = useMemo(() => {
    return props.value ? props.value.split(',').filter(Boolean) : [];
  }, [props.value]);

  const containsText = (text: string, searchText: string) =>
    text?.toLowerCase().indexOf(searchText?.toLowerCase()) > -1;

  const fetchLists = useCallback(() => {
    const customerIds = props.customerIds || [];
    
    getAllProjectsFilter(customerIds)
      .then(response => {
        const projects = response.data.projects || [];
        const filteredProjects = projects.filter(project => !project.isClosed);
        setList(filteredProjects);
        if (selectedValues.length > 0) {
          const validSelections = selectedValues.filter(id => 
            filteredProjects.some(project => project.projectId === id)
          );

          if (validSelections.length !== selectedValues.length) {
            props.onChange(validSelections.join(','));
          }
        }
      })
      .catch(error => {
        setList([]);

        if (selectedValues.length > 0) {
          props.onChange('');
        }
      });
  }, [props.customerIds, selectedValues, props.onChange]);

  const filteredLists = useMemo(() => {
    return list?.filter((option) => 
      containsText(option?.projectName, searchText)
    ) || [];
  }, [searchText, list]);

  const handleSelectChange = (event: any) => {
    const values = event.target.value;
    props.onChange(values.join(','));
  };

  useEffect(() => {
 
    const currentCustomerIds = props.customerIds || [];
    const customerIdsChanged = JSON.stringify(currentCustomerIds) !== JSON.stringify(prevCustomerIds);
    
    if (customerIdsChanged) {
      fetchLists();
      setPrevCustomerIds(currentCustomerIds);
    }
  }, [props.customerIds, prevCustomerIds, fetchLists]);

  useEffect(() => {
    fetchLists();
  }, []);

  return (
    <FormControl fullWidth disabled={props?.disabled || false}>
      <InputLabel style={{ fontSize: '12px' }}>
        {props?.title || ''}
      </InputLabel>
      <Select
        id="filter-project"
        sx={{ mt: 1 }}
        style={{
          width: props?.width || '100%',
          fontSize: '12px',
          height: '2rem'
        }}
        size="small"
        multiple
        value={selectedValues}
        onChange={handleSelectChange}
        onClose={() => setSearchText('')}
        renderValue={(selected) => {
          return selected
            .map((id) => {
              const selectedItem = list.find((item) => item.projectId === id);
              return selectedItem?.projectName || id; 
            })
            .filter(Boolean)
            .join(', ');
        }}
      >
        <ListSubheader>
          <TextField
            size="small"
            autoFocus
            placeholder="Type to search..."
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
            onClick={(e) => e.stopPropagation()}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Escape') e.stopPropagation();
            }}
          />
        </ListSubheader>
        
        {filteredLists.length > 0 ? (
          filteredLists.map((item) => (
            <MenuItem key={item.projectId} value={item.projectId}>
              <Checkbox checked={selectedValues.includes(item.projectId)} />
              <ListItemText primary={item.projectName} />
            </MenuItem>
          ))
        ) : (
          <Typography
            style={{
              fontSize: '14px',
              margin: '1rem',
              textAlign: 'center',
              color: "grey"
            }}
            variant="body2"
          >
             {"< - "}There are no projects created for this client{" - >"}
          </Typography>
        )}
      </Select>
    </FormControl>
  );
});

export default ProjectDropdownFilter;